import { usePayStore } from '@/app/(dona)/layout'
import PaymentAPIService from '@/services/PaymentAPIService'
import debugLogger from '@/utils/debugLogger'
import { DONATION_PATH_ROOT } from '@/utils/donationPaths'
import { formatDayMonthForApi } from '@/utils/paymentUtils'
import { iFrameUrlBuilder } from '@/utils/recurrentPayment'
import { useCallback } from 'react'

/**
 * Hook for executing payment operations (API calls, data formatting)
 */
export const usePaymentExecutor = () => {
  const { store, setStore } = usePayStore()
  const paymentClientId = process.env.paymentClientId

  const executeSinglePayment = useCallback(
    async (formData, options = {}) => {
      const {
        paymentSource,
        searchParams = {},
        pathPrefix = DONATION_PATH_ROOT,
      } = options

      debugLogger.log('Executor', 'Executing Single Payment', {
        paymentSource,
        options,
      })

      if (!formData || !paymentSource) {
        debugLogger.error('Executor', 'Missing required payment data')
        return {
          success: false,
          error: new Error('Missing required payment data'),
        }
      }

      const serverPaymentMethod =
        paymentSource === 'creditcard' ? 'creditcard-2022' : paymentSource

      try {
        console.log(
          '[PaymentExecutor] Executing single payment:',
          paymentSource
        )

        setStore((prev) => ({
          ...prev,
          formValues: {
            ...prev?.formValues,
            ...formData,
            donatorPaymentMethod: paymentSource,
          },
        }))

        const payResult = await PaymentAPIService.createPaymentOrder(
          formData,
          paymentClientId,
          serverPaymentMethod,
          searchParams
        )

        debugLogger.log('Executor', 'Order Result', payResult)

        if (payResult.success) {
          setStore((prev) => ({
            ...prev,
            paymentInfo: {
              ckey: payResult.ckey,
              orderID: payResult.orderID,
              token: payResult.token,
            },
          }))
        }

        return {
          ...payResult,
          paymentType: 'single',
          requiresPopup: !!payResult.approvalLink,
          successUrl:
            payResult.ckey || payResult.token || payResult.orderID
              ? `${pathPrefix}/confirm?ckey=${payResult.ckey}&token=${payResult.token || ''}&orderID=${payResult.orderID || ''}`
              : `${pathPrefix}/confirm`,
          failUrl: `${pathPrefix}/errore`,
        }
      } catch (error) {
        debugLogger.error('Executor', 'Single payment error', error)
        console.error('[PaymentExecutor] Single payment error:', error)
        return { success: false, error, paymentType: 'single' }
      }
    },
    [paymentClientId, setStore]
  )

  const executeRecurrentPayment = useCallback(
    async (formData) => {
      if (!formData) {
        return {
          success: false,
          error: new Error('Missing form data for recurrent payment'),
        }
      }

      try {
        debugLogger.log('Executor', 'Executing Recurrent Payment')
        console.log('[PaymentExecutor] Executing recurrent payment')

        setStore((prev) => ({
          ...prev,
          formValues: { ...prev?.formValues, ...formData },
        }))

        // Persist the anniversary date so the confirm popup (same-origin, but a
        // separate browsing context that doesn't share React state) can include
        // it in the confirmPayment payload.
        if (typeof window !== 'undefined') {
          try {
            window.localStorage.setItem(
              'recurrentDataAnniversary',
              formatDayMonthForApi(
                formData['donationInMemoryReceiverAnniversaryDate'] || ''
              )
            )
          } catch {
            /* ignore storage errors */
          }
        }

        const iframeUrl = iFrameUrlBuilder(formData)

        debugLogger.log('Executor', 'Iframe URL generated', iframeUrl)
        console.log('[PaymentExecutor] Iframe URL generated:', iframeUrl)

        return {
          success: true,
          approvalLink: iframeUrl,
          iframeUrl,
          paymentType: 'recurrent',
          requiresPopup: true,
        }
      } catch (error) {
        debugLogger.error('Executor', 'Recurrent payment error', error)
        console.error('[PaymentExecutor] Recurrent payment error:', error)
        return { success: false, error, paymentType: 'recurrent' }
      }
    },
    [setStore]
  )

  const getSuccessUrl = useCallback(
    (orderData, paymentType, pathPrefix = DONATION_PATH_ROOT) => {
      if (paymentType === 'recurrent') return `${pathPrefix}/confirm`

      const { ckey, token, orderID } = orderData || {}
      if (ckey) {
        return `${pathPrefix}/confirm?ckey=${ckey}&token=${token || ''}&orderID=${orderID || ''}`
      }

      return `${pathPrefix}/confirm`
    },
    []
  )

  return {
    executeSinglePayment,
    executeRecurrentPayment,
    getSuccessUrl,
  }
}

export default usePaymentExecutor
